// Central de notificações — o estado do sino e o socket que o alimenta.
//
// Out/26. Primeira entrega do Cetto Copiloto.
//
// ─── Por que FORA do React ─────────────────────────────────────────────
// O sino vive na TopBar, e cada uma das ~11 telas monta a sua própria
// TopBar. Se o socket morasse no componente, cada clique no menu lateral
// fecharia e reabriria a conexão — e um aviso que chegasse no meio da
// troca de tela se perderia. Aqui ele é um módulo: abre uma vez por aba e
// sobrevive à navegação. Os componentes só ASSINAM o estado.
//
// ─── O que o servidor garante, e o que fica com a gente ────────────────
// O badge vem de `GET /notifications/unread-count` uma vez, e depois só o
// socket o move (sem polling — requisito do documento). Isso só é correto
// se nada se perder enquanto o socket estiver caído; por isso, toda vez
// que ele RECONECTA, a contagem e a primeira página são relidas.
//
// ─── Troca de usuário na mesma aba ─────────────────────────────────────
// O logout só apaga o token do localStorage — o módulo continua vivo. Sem
// cuidado, o socket do usuário anterior seguiria aberto e os avisos dele
// apareceriam no sino de quem entrou depois. A cada assinatura conferimos
// se o token mudou; se mudou, tudo é descartado e recomeça.
import api from './Api';

const PAGINA = 20;

let estado = novoEstado();
const ouvintes = new Set();

let ws = null;
let tokenDoSocket = null;
let tentativas = 0;
let timerReconexao = null;
let heartbeat = null;
let recusado = false;   // 4401: não insistir com o mesmo token

function novoEstado() {
  return {
    itens: [],
    naoLidas: 0,
    proximoCursor: null,
    carregado: false,      // a primeira página da lista já veio?
    carregando: false,
    erro: false,
  };
}

function publicar(parcial) {
  estado = { ...estado, ...parcial };
  ouvintes.forEach((fn) => { try { fn(estado); } catch {} });
}

// ─── Socket ────────────────────────────────────────────────────────────

function urlDoSocket() {
  const base = (process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br')
    .replace('https://', 'wss://').replace('http://', 'ws://');
  // Sob /ws/ de propósito: é o caminho que o Nginx já repassa como
  // WebSocket. Ver backend/app/routers/websocket.py.
  return `${base}/ws/usuario`;
}

function pararSocket() {
  if (timerReconexao) { clearTimeout(timerReconexao); timerReconexao = null; }
  if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
  if (ws) {
    const velho = ws;
    ws = null;
    velho.onclose = null;   // fechar de propósito não agenda reconexão
    try { velho.close(1000, 'encerrado'); } catch {}
  }
}

function agendarReconexao() {
  if (timerReconexao || recusado) return;
  // 2s, 4s, 8s… até 30s. Servidor fora do ar não vira martelada.
  const espera = Math.min(30000, 2000 * 2 ** Math.min(tentativas, 4));
  tentativas += 1;
  timerReconexao = setTimeout(() => { timerReconexao = null; abrirSocket(); }, espera);
}

function abrirSocket() {
  const token = localStorage.getItem('token');
  if (!token || recusado) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  tokenDoSocket = token;
  let socket;
  try {
    socket = new WebSocket(urlDoSocket());
  } catch {
    agendarReconexao();
    return;
  }
  ws = socket;

  socket.onopen = () => {
    // Token no PRIMEIRO frame, nunca na URL — o Nginx grava a URL no log.
    try { socket.send(JSON.stringify({ type: 'auth', token })); } catch {}
  };

  socket.onmessage = (ev) => {
    let dado;
    try { dado = JSON.parse(ev.data); } catch { return; }

    if (dado.type === 'auth_ok') {
      const eraReconexao = tentativas > 0;
      tentativas = 0;
      if (heartbeat) clearInterval(heartbeat);
      heartbeat = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          try { socket.send(JSON.stringify({ type: 'ping' })); } catch {}
        }
      }, 25000);
      // Reconexão: o que chegou com o socket caído não veio por ele.
      if (eraReconexao) sincronizar();
      return;
    }

    if (dado.type === 'notification:new' && dado.notification) {
      const n = dado.notification;
      // Dedup: a mesma aba pode reler a página depois de reconectar e
      // receber o mesmo aviso pelo socket logo em seguida.
      if (estado.itens.some((x) => x.id === n.id)) return;
      publicar({
        itens: estado.carregado ? [n, ...estado.itens] : estado.itens,
        naoLidas: estado.naoLidas + (n.read ? 0 : 1),
      });
    }
  };

  socket.onclose = (ev) => {
    if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
    if (ws === socket) ws = null;
    // 4401 = token recusado. Reabrir com o mesmo token daria a mesma
    // resposta; quem resolve é o próximo login.
    if (ev.code === 4401) { recusado = true; return; }
    agendarReconexao();
  };
}

// ─── REST ──────────────────────────────────────────────────────────────

async function lerContagem() {
  try {
    const r = await api.get('/notifications/unread-count');
    publicar({ naoLidas: r.data?.count || 0 });
  } catch {
    // Sem a contagem o sino só fica sem número; o socket segue.
  }
}

async function sincronizar() {
  await lerContagem();
  if (estado.carregado) await carregar({ recomecar: true });
}

/** Carrega a primeira página (ou a seguinte, com `mais`). */
export async function carregar({ mais = false, recomecar = false } = {}) {
  if (estado.carregando) return;
  if (mais && !estado.proximoCursor) return;
  publicar({ carregando: true, erro: false });
  try {
    const params = { limit: PAGINA };
    if (mais) params.cursor = estado.proximoCursor;
    const r = await api.get('/notifications', { params });
    const novos = r.data?.items || [];
    let itens;
    if (mais) {
      const vistos = new Set(estado.itens.map((x) => x.id));
      itens = [...estado.itens, ...novos.filter((x) => !vistos.has(x.id))];
    } else {
      itens = novos;
    }
    publicar({
      itens,
      proximoCursor: r.data?.next_cursor || null,
      carregado: true,
      carregando: false,
    });
    if (recomecar) await lerContagem();
  } catch {
    publicar({ carregando: false, erro: true });
  }
}

/** Marca uma como lida. Otimista: o sino responde na hora do clique. */
export async function marcarLida(id) {
  const alvo = estado.itens.find((x) => x.id === id);
  if (!alvo || alvo.read) return;
  publicar({
    itens: estado.itens.map((x) => (x.id === id ? { ...x, read: true } : x)),
    naoLidas: Math.max(0, estado.naoLidas - 1),
  });
  try {
    await api.patch(`/notifications/${id}/read`);
  } catch {
    // Falhou: volta, pra não mentir que leu.
    publicar({
      itens: estado.itens.map((x) => (x.id === id ? { ...x, read: false } : x)),
      naoLidas: estado.naoLidas + 1,
    });
  }
}

export async function marcarTodas() {
  if (!estado.naoLidas) return;
  const antes = estado;
  publicar({ itens: estado.itens.map((x) => ({ ...x, read: true })), naoLidas: 0 });
  try {
    await api.patch('/notifications/read-all');
  } catch {
    publicar({ itens: antes.itens, naoLidas: antes.naoLidas });
  }
}

// ─── Assinatura ────────────────────────────────────────────────────────

/** O componente assina e recebe o estado a cada mudança. Devolve o cancelamento. */
export function assinar(fn) {
  const token = localStorage.getItem('token');

  // Trocou de usuário na mesma aba (logout + login sem recarregar): nada
  // do anterior pode sobreviver — nem socket, nem lista, nem badge.
  if (tokenDoSocket && token !== tokenDoSocket) {
    pararSocket();
    estado = novoEstado();
    tokenDoSocket = null;
    tentativas = 0;
    recusado = false;
  }

  ouvintes.add(fn);
  fn(estado);

  if (token && !ws && !timerReconexao && !recusado) {
    lerContagem();
    abrirSocket();
  }

  // Ao desassinar NÃO fechamos o socket: é a troca de tela, e a próxima
  // TopBar assina em milissegundos. Fechar aqui é exatamente o que este
  // módulo existe pra evitar.
  return () => { ouvintes.delete(fn); };
}
